import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/NavBar/NavBar';
import LoadingAnimation from '../components/LoadingAnimation/LoadingAnimation';
import CoachAssessmentModal from '../components/CoachAssessmentModal/CoachAssessmentModal';
import CoachCheckinModal from '../components/CoachCheckinModal/CoachCheckinModal';
import {
  createCoachAccount,
  getMyCoachAccount,
  listCoachAlerts,
  listMyMembers,
  markAssessmentReviewed,
  markWeeklyCheckinReviewed,
  removeCoachMember,
} from '../api/coach';
import { supabase } from '../supabaseClient';
import './coach.css';

function formatMacros(program) {
  if (!program) return null;
  const p = program.protein_g ?? null;
  const c = program.carbs_g ?? null;
  const f = program.fats_g ?? null;
  const kcal = program.calories ?? null;
  const parts = [];
  if (kcal != null) parts.push(`${Math.round(kcal)} kcal`);
  if (p != null || c != null || f != null) parts.push(`${Math.round(p || 0)}P / ${Math.round(c || 0)}C / ${Math.round(f || 0)}F`);
  return parts.join(' | ') || null;
}

function formatAlertDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString();
}

export default function CoachDashboard() {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');

  const [coachAccount, setCoachAccount] = useState(null);
  const [members, setMembers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState('');
  const [alertsFilter, setAlertsFilter] = useState('all');
  const [memberSearch, setMemberSearch] = useState('');
  const [removingMemberId, setRemovingMemberId] = useState('');
  const [alertSearch, setAlertSearch] = useState('');
  const [reviewingKey, setReviewingKey] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailType, setDetailType] = useState('');
  const [detailMemberName, setDetailMemberName] = useState('');
  const [detailAssessment, setDetailAssessment] = useState(null);
  const [detailCheckin, setDetailCheckin] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [{ data: acct, error: aErr }, { data: roster, error: rErr }] = await Promise.all([
        getMyCoachAccount(),
        listMyMembers(),
      ]);
      if (aErr) throw aErr;
      if (rErr) throw rErr;
      setCoachAccount(acct);
      setMembers(roster || []);
      setAlertsError('');
      return roster || [];
    } catch (e) {
      setError(e?.message || 'Unable to load coach dashboard.');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAlerts = useCallback(async (roster = []) => {
    setAlertsLoading(true);
    setAlertsError('');
    try {
      const { data, error: aErr } = await listCoachAlerts();
      if (aErr) throw aErr;
      const nameById = new Map(
        (roster || []).map((m) => [m.memberId, m.assessment?.name || 'Member'])
      );
      const next = [];
      (data?.assessments || []).forEach((row) => {
        next.push({
          key: `assessment-${row.user_id}`,
          type: 'assessment',
          memberId: row.user_id,
          memberName: nameById.get(row.user_id) || row.name || 'Member',
          weight: row.weight_lbs ?? null,
          submittedAt: row.submitted_at || null,
        });
      });
      (data?.checkins || []).forEach((row) => {
        next.push({
          key: `checkin-${row.id}`,
          type: 'checkin',
          memberId: row.userid,
          memberName: nameById.get(row.userid) || 'Member',
          weight: row.weight ?? null,
          submittedAt: row.createdat || null,
          checkinId: row.id,
        });
      });
      next.sort((a, b) => {
        const aTime = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
        const bTime = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
        return bTime - aTime;
      });
      setAlerts(next);
    } catch (e) {
      setAlertsError(e?.message || 'Unable to load alerts.');
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    const roster = await load();
    await loadAlerts(roster);
  }, [load, loadAlerts]);

  useEffect(() => {
    const bootstrap = async () => {
      await refreshAll();
    };
    bootstrap();
  }, [refreshAll]);

  const memberCards = useMemo(() => (members || []).map((m) => {
    const a = m.assessment;
    const program = m.program;
    const name = a?.name || 'Member';
    const details = [
      a?.age != null ? `Age ${a.age}` : null,
      a?.weight_lbs != null ? `${a.weight_lbs} lb` : null,
      program?.goal ? program.goal : null,
      formatMacros(program),
    ].filter(Boolean).join(' | ');
    return { ...m, name, details };
  }), [members]);

  const normalizedMemberQuery = memberSearch.trim().toLowerCase();
  const filteredMembers = useMemo(() => {
    if (!normalizedMemberQuery) return memberCards;
    return memberCards.filter((m) => m.name?.toLowerCase().includes(normalizedMemberQuery));
  }, [memberCards, normalizedMemberQuery]);

  const filteredAlerts = useMemo(() => {
    let next = alerts;
    if (alertsFilter === 'assessments') next = alerts.filter((a) => a.type === 'assessment');
    if (alertsFilter === 'checkins') next = alerts.filter((a) => a.type === 'checkin');
    const normalizedAlertQuery = alertSearch.trim().toLowerCase();
    if (!normalizedAlertQuery) return next;
    return next.filter((a) => a.memberName?.toLowerCase().includes(normalizedAlertQuery));
  }, [alerts, alertsFilter, alertSearch]);

  const handleMarkReviewed = async (item) => {
    if (!item) return;
    setReviewingKey(item.key);
    setAlertsError('');
    try {
      if (item.type === 'assessment') {
        const { error: aErr } = await markAssessmentReviewed(item.memberId);
        if (aErr) throw aErr;
      } else {
        const { error: cErr } = await markWeeklyCheckinReviewed(item.checkinId);
        if (cErr) throw cErr;
      }
      setAlerts((prev) => prev.filter((a) => a.key !== item.key));
    } catch (e) {
      setAlertsError(e?.message || 'Unable to mark reviewed.');
    } finally {
      setReviewingKey('');
    }
  };

  const handleViewAlert = async (item) => {
    if (!item) return;
    setDetailOpen(true);
    setDetailType(item.type);
    setDetailMemberName(item.memberName || 'Member');
    setDetailAssessment(null);
    setDetailCheckin(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      if (item.type === 'assessment') {
        const { data, error: aErr } = await supabase
          .from('assessments')
          .select('user_id, name, age, gender, height_feet, height_inches, weight_lbs, assessment')
          .eq('user_id', item.memberId)
          .maybeSingle();
        if (aErr) throw aErr;
        setDetailAssessment(data || null);
      } else {
        const { data, error: cErr } = await supabase
          .from('weekly_checkins')
          .select('*')
          .eq('id', item.checkinId)
          .maybeSingle();
        if (cErr) throw cErr;
        setDetailCheckin(data || null);
      }
    } catch (e) {
      setDetailError(e?.message || 'Unable to load details.');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailType('');
    setDetailAssessment(null);
    setDetailCheckin(null);
    setDetailLoading(false);
    setDetailError('');
  };

  const onCreateOrRotate = async () => {
    setCreating(true);
    setInviteCode('');
    setError('');
    try {
      const { data, error: cErr } = await createCoachAccount({ displayName });
      if (cErr) throw cErr;
      setInviteCode(data?.invite_code || '');
      await load();
    } catch (e) {
      setError(e?.message || 'Unable to generate invite code.');
    } finally {
      setCreating(false);
    }
  };

  const copyInvite = async () => {
    if (!inviteCode) return;
    try {
      await navigator.clipboard.writeText(inviteCode);
    } catch (_) {}
  };

  const handleRemoveMember = async (member) => {
    if (!member?.membershipId) return;
    const confirmed = window.confirm(
      `Remove ${member.name || 'this client'} from your coaching roster? They will no longer appear on your coach dashboard.`
    );
    if (!confirmed) return;

    setRemovingMemberId(member.membershipId);
    setError('');
    try {
      const { error: removeErr } = await removeCoachMember(member.membershipId);
      if (removeErr) throw removeErr;
      await refreshAll();
    } catch (e) {
      setError(e?.message || 'Unable to remove client.');
    } finally {
      setRemovingMemberId('');
    }
  };

  return (
    <>
      <NavBar />
      {loading && <LoadingAnimation fullScreen />}
      <main className="web-view">
        <div className="container">
          <div className="coach-page">
            <div className="coach-header">
              <h1 className="coach-title">Coach Dashboard</h1>
              <button type="button" className="coach-btn secondary" onClick={refreshAll} disabled={loading || creating || alertsLoading}>Refresh</button>
            </div>

            {error && <div className="coach-alert error">{error}</div>}

            <section className="coach-card">
              <div className="coach-card-header">
                <h2 className="coach-card-title">Invite</h2>
              </div>
              <div className="coach-card-body">
                <p className="coach-muted">
                  Share your invite code with a member so they can link to your coaching account.
                </p>

                <div className="coach-row">
                  <label className="coach-label" htmlFor="coach-display-name">Coach name (optional)</label>
                  <input
                    id="coach-display-name"
                    className="coach-input"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Displayed to members"
                  />
                </div>

                <div className="coach-actions">
                  <button type="button" className="coach-btn" onClick={onCreateOrRotate} disabled={creating}>
                    {coachAccount ? 'Generate New Invite Code' : 'Create Coaching Account'}
                  </button>
                  {coachAccount?.invite_code_last4 && (
                    <span className="coach-muted">Current code ends with {coachAccount.invite_code_last4}</span>
                  )}
                </div>

                {inviteCode && (
                  <div className="coach-invite">
                    <div className="coach-invite-code">{inviteCode}</div>
                    <button type="button" className="coach-btn secondary" onClick={copyInvite}>Copy</button>
                  </div>
                )}
              </div>
            </section>

            <section className="coach-card">
              <div className="coach-card-header">
                <h2 className="coach-card-title">Members</h2>
                <div className="coach-muted">{filteredMembers.length} total</div>
              </div>
              <div className="coach-card-body">
                {!coachAccount && (
                  <div className="coach-alert">
                    Create a coaching account to start inviting members.
                  </div>
                )}

                {coachAccount && memberCards.length === 0 && (
                  <div className="coach-alert">
                    No members yet. Share your invite code and have them join from their Profile page.
                  </div>
                )}

                {coachAccount && memberCards.length > 0 && (
                  <div className="coach-search-row">
                    <input
                      className="coach-input coach-search-input"
                      type="search"
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      placeholder="Search members by name"
                      aria-label="Search members by name"
                    />
                  </div>
                )}

                {coachAccount && memberCards.length > 0 && filteredMembers.length === 0 ? (
                  <div className="coach-alert">No members match that search.</div>
                ) : (
                  <div className="coach-grid">
                    {filteredMembers.map((m) => (
                      <div key={m.memberId} className="coach-member-card">
                        <Link to={`/coach/members/${m.memberId}`} className="coach-member-link">
                          <div className="coach-member-name">{m.name}</div>
                          {m.details ? <div className="coach-member-details">{m.details}</div> : <div className="coach-muted">No details yet</div>}
                        </Link>
                        <div className="coach-member-actions">
                          <Link to={`/coach/members/${m.memberId}`} className="coach-btn secondary coach-member-action-link">
                            Open
                          </Link>
                          <button
                            type="button"
                            className="coach-btn danger"
                            onClick={() => handleRemoveMember(m)}
                            disabled={removingMemberId === m.membershipId}
                          >
                            {removingMemberId === m.membershipId ? 'Removing...' : 'Remove'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <section className="coach-card">
              <div className="coach-card-header">
                <h2 className="coach-card-title">Alerts</h2>
                <div className="coach-muted">{filteredAlerts.length} need review</div>
              </div>
              <div className="coach-card-body">
                <div className="coach-search-row">
                  <input
                    className="coach-input coach-search-input"
                    type="search"
                    value={alertSearch}
                    onChange={(e) => setAlertSearch(e.target.value)}
                    placeholder="Search alerts by name"
                    aria-label="Search alerts by name"
                  />
                </div>
                <div className="coach-filter-row">
                  <button
                    type="button"
                    className={`coach-filter-btn ${alertsFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setAlertsFilter('all')}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`coach-filter-btn ${alertsFilter === 'assessments' ? 'active' : ''}`}
                    onClick={() => setAlertsFilter('assessments')}
                  >
                    Assessments
                  </button>
                  <button
                    type="button"
                    className={`coach-filter-btn ${alertsFilter === 'checkins' ? 'active' : ''}`}
                    onClick={() => setAlertsFilter('checkins')}
                  >
                    Check-Ins
                  </button>
                </div>

                {alertsError && <div className="coach-alert error">{alertsError}</div>}

                {alertsLoading ? (
                  <div className="coach-muted">Loading alerts...</div>
                ) : filteredAlerts.length === 0 ? (
                  <div className="coach-alert">
                    {alerts.length > 0 ? 'No alerts match that search.' : 'No submissions need review.'}
                  </div>
                ) : (
                  <div className="coach-alert-list">
                    {filteredAlerts.map((item) => {
                      const dateLabel = formatAlertDate(item.submittedAt);
                      const typeLabel = item.type === 'assessment' ? 'Assessment' : 'Check-In';
                      const metaParts = [typeLabel, dateLabel].filter(Boolean).join(' • ');
                      return (
                        <div key={item.key} className="coach-alert-item">
                          <div className="coach-alert-info">
                            <div className="coach-alert-title">{item.memberName}</div>
                            {metaParts && <div className="coach-alert-meta">{metaParts}</div>}
                            <div className="coach-alert-flag">Needs Review</div>
                          </div>
                          <div className="coach-alert-actions">
                            <button
                              type="button"
                              className="coach-btn secondary"
                              onClick={() => handleViewAlert(item)}
                            >
                              View
                            </button>
                            <button
                              type="button"
                              className="coach-btn"
                              onClick={() => handleMarkReviewed(item)}
                              disabled={reviewingKey === item.key}
                            >
                              {reviewingKey === item.key ? 'Reviewing...' : 'Mark reviewed'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>
      <CoachAssessmentModal
        isOpen={detailOpen && detailType === 'assessment'}
        onClose={closeDetail}
        memberName={detailMemberName}
        assessmentRow={detailAssessment}
        loading={detailLoading}
        error={detailError}
      />
      <CoachCheckinModal
        isOpen={detailOpen && detailType === 'checkin'}
        onClose={closeDetail}
        memberName={detailMemberName}
        checkinRow={detailCheckin}
        loading={detailLoading}
        error={detailError}
      />
    </>
  );
}
